import { Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from '@foxera/environments/enviroment';
import {
  SIGNALR_WORKOUT_EVENTS,
  SIGNALR_WORKOUT_HUB,
  SIGNALR_WORKOUT_METHODS,
} from '@foxera/feature/clients/constants/signalr-workout.constants';
import { IWorkoutPlanJob } from '@foxera/shared/interfaces/client.interface';
import { TokenService } from '@foxera/shared/services/token.service';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

@Injectable({
  providedIn: 'root',
})
export class SignalrWorkoutJobService implements OnDestroy {
  constructor(private tokenService: TokenService) {}

  private hubConnection: HubConnection | null = null;
  private currentEmployeeId: number | null = null;
  private currentTenantId: string | null = null;
  private isInitialized = false;

  public readonly jobUpdated = signal<IWorkoutPlanJob | null>(null);
  public readonly connectionState = signal<HubConnectionState>(HubConnectionState.Disconnected);

  async initialize(): Promise<void> {
    if (this.isInitialized && this.hubConnection?.state === HubConnectionState.Connected) {
      return;
    }

    try {
      const token = this.tokenService.getToken();
      if (!token) {
        return;
      }

      const hubUrl = `${environment.api_fitness_base_url_signalr}${SIGNALR_WORKOUT_HUB.ENDPOINT}`;

      this.hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => this.tokenService.getToken() || '',
        })
        .configureLogging(LogLevel.Warning)
        .withAutomaticReconnect()
        .build();

      this.setupEventListeners();

      this.hubConnection.onreconnecting(() => {
        this.connectionState.set(HubConnectionState.Reconnecting);
      });

      this.hubConnection.onreconnected(async () => {
        this.connectionState.set(HubConnectionState.Connected);
        if (this.currentEmployeeId !== null && this.currentTenantId) {
          await this.subscribeToEmployee(this.currentEmployeeId, this.currentTenantId);
        }
      });

      this.hubConnection.onclose((error) => {
        this.connectionState.set(HubConnectionState.Disconnected);
      });

      await this.hubConnection.start();
      this.isInitialized = true;
      this.connectionState.set(HubConnectionState.Connected);

      if (this.currentEmployeeId !== null && this.currentTenantId) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        await this.subscribeToEmployee(this.currentEmployeeId, this.currentTenantId);
      }
    } catch (error) {
      this.connectionState.set(HubConnectionState.Disconnected);
    }
  }

  private setupEventListeners(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on(SIGNALR_WORKOUT_EVENTS.JOB_UPDATED, (job: IWorkoutPlanJob) => {
      this.jobUpdated.set(job);
    });
  }

  async subscribeToEmployee(employeeId: number, tenantId: string): Promise<void> {
    if (!this.hubConnection) {
      await this.initialize();
    }

    let retries = 0;
    const maxRetries = 10;
    while (
      this.hubConnection &&
      this.hubConnection.state !== HubConnectionState.Connected &&
      retries < maxRetries
    ) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      retries++;
    }

    if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
      return;
    }

    try {
      await this.hubConnection.invoke(
        SIGNALR_WORKOUT_METHODS.SUBSCRIBE,
        employeeId,
        tenantId
      );
      
      this.currentEmployeeId = employeeId;
      this.currentTenantId = tenantId;
    } catch (error) {
      throw error;
    }
  }

  async unsubscribeFromEmployee(): Promise<void> {
    if (
      !this.hubConnection ||
      this.hubConnection.state !== HubConnectionState.Connected ||
      this.currentEmployeeId === null ||
      !this.currentTenantId
    ) {
      return;
    }

    try {
      await this.hubConnection.invoke(
        SIGNALR_WORKOUT_METHODS.UNSUBSCRIBE,
        this.currentEmployeeId,
        this.currentTenantId
      );
      this.currentEmployeeId = null;
      this.currentTenantId = null;
    } catch {
      // Ignore unsubscribe errors on disconnect
    }
  }

  isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  async disconnect(): Promise<void> {
    await this.unsubscribeFromEmployee();

    if (this.hubConnection) {
      try {
        await this.hubConnection.stop();
      } catch {
        // Ignore stop errors
      }
      this.hubConnection = null;
    }

    this.currentEmployeeId = null;
    this.currentTenantId = null;
    this.isInitialized = false;
    this.connectionState.set(HubConnectionState.Disconnected);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
